import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { auth, db } from "../firebase";
import { DEFAULT_PLAN_ID, getPlanDefinition } from "../plans";
import { fallbackUsernameFromEmail, formatPublicHandle, getPublicUsername } from "../utils/identity";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (nextUser) => {
      setUser(nextUser);
      setAuthLoading(false);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!user) {
      setUserProfile(null);
      return undefined;
    }

    const unsubscribe = onSnapshot(doc(db, "users", user.uid), (snapshot) => {
      const data = snapshot.data() || {};
      const plan = getPlanDefinition(data.plan || DEFAULT_PLAN_ID);
      const workspaceId = data.workspaceId || user.uid;

      setUserProfile({
        ...data,
        email: data.email || user.email,
        username: getPublicUsername(data, data.email || user.email),
        publicHandle: formatPublicHandle(data, data.email || user.email),
        workspaceId,
        workspaceName: data.workspaceName || "My Workspace",
        workspaceRole: data.workspaceRole || "owner",
        appRole: data.appRole || "member",
        isAdmin: (data.appRole || "member") === "admin",
        planId: plan.id,
        planName: data.planName || plan.name,
        seatLimit: data.seatLimit || plan.userLimit,
        programLimit: data.programLimit || plan.programLimit
      });
    });

    return unsubscribe;
  }, [user]);

  useEffect(() => {
    if (!userProfile?.workspaceId) {
      setWorkspace(null);
      return undefined;
    }

    const unsubscribe = onSnapshot(doc(db, "workspaces", userProfile.workspaceId), (snapshot) => {
      const data = snapshot.data();

      if (!data) {
        setWorkspace(null);
        return;
      }

      const plan = getPlanDefinition(data.plan || userProfile.planId || DEFAULT_PLAN_ID);

      setWorkspace({
        id: snapshot.id,
        ...data,
        ownerUsername: data.ownerUsername || fallbackUsernameFromEmail(userProfile?.email),
        planId: plan.id,
        planName: data.planName || plan.name,
        seatLimit: data.seatLimit || plan.userLimit,
        programLimit: data.programLimit || plan.programLimit
      });
    });

    return unsubscribe;
  }, [userProfile?.email, userProfile?.workspaceId, userProfile?.planId]);

  const value = useMemo(
    () => ({
      user,
      userProfile,
      workspace,
      authLoading
    }),
    [user, userProfile, workspace, authLoading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used inside AuthProvider");
  }

  return context;
}
